import { Empty, Space, Table, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { isNull } from "lodash";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import { ApiNames } from "../../api/api";
import { ReactComponent as ClaimsIcon } from "../../assets/svg/claims-table-icon.svg";
import { BLACK } from "../../constant/colors";
import { SUPPORTED_COVERAGES } from "../../constant/patient";
import { Routes } from "../../constant/routes";
import { fontWeights, sizes } from "../../constant/styles";
import { PAGE_SIZE, selectedViews } from "../../constant/table";
import ACTION from "../../store/action";
import {
  selectAllClaimsData,
  selectFiltersModified,
  selectIsFetching,
  selectTableColumns,
  selectUserOrGroupById
} from "../../store/selector";
import { dateFormatter } from "../../utils/date";
import { fetchTableData } from "../../utils/fetchTableData";
import { formatToDecimal, buildAssigneeEditProps } from "../../utils/formaters";
import EditableCell from "@tailormed/common-client/component/inline-edit/EditableCell";
import TextEditor from "@tailormed/common-client/component/inline-edit/editors/TextEditor";
import DateEditor from "@tailormed/common-client/component/inline-edit/editors/DateEditor";
import DropdownEditor from "@tailormed/common-client/component/inline-edit/editors/DropdownEditor";
import SearchableDropdownEditor from "@tailormed/common-client/component/inline-edit/editors/SearchableDropdownEditor";
import InlineEmptyPlusButton from "@tailormed/common-client/component/inline-edit/InlineEmptyPlusButton";
import useInlineEditClaim from "../../hooks/InlineEdit/useInlineEditClaim";
import useTableInlineEdit from "../../hooks/InlineEdit/useTableInlineEdit";
import { handleRowNavigation } from "../../utils/tableRowNavigation";
import { isEditableClaimColumn } from "./InlineEdit/inlineEditConfig";
import { statuses as claimStatusOptions, PENDING, OOP, NEW_PENDING_PAYMENT, PAID } from "../../constant/claim";
import {
  CLAIM_STATUS_COLORS,
  getStatusTagStyle,
  getStatusTagHoverBackground,
  getStatusTagHoverBorder
} from "../../util/statusTagStyles";
import { ColumnSelector } from "../ColumnSelector";
import { StyledGreenValue, StyledRedValue } from "../Patient/PatientApplications/PatientApplicationClaim/styles";
import { patientInsuranceCell } from "../TableCells";
import { TailormedSpinner } from "../TailormedSpinner/TailormedSpinner";
import LinkableCell from "../UI/LinkableCell";
import AllClaimsPageCount from "./ClaimsPageCount";
import {
  ClaimAssigneeColumn,
  ClaimLastSavedByColumn,
  DrugColumnComponent as MedicationsColumnComponent,
  ClaimStatusColumn,
  DateOfServiceColumn,
  EnrollmentAssigneeColumn,
  PatientNameColumn,
  StarColumn,
  withSorting
} from "./CommonColumns";
import ConsentStatusColumn from "./ConsentStatusColumn";
import { ColumnSelectorContainer } from "./TableStyle";
import { commonBulkActionsTableStyle } from "./BulkActionsTableStyle";
import useColumnResizeAndReorder from "../../hooks/useColumnResizeAndReorder";
import useTrackSavedListLayout from "../../hooks/useTrackSavedListLayout";
import { TableColumnWrapper } from "@tailormed/common-client/component/ColumnResizeAndReorder/TableColumnWrapper";
import { ColumnsKeys } from "../../constant/columns";

const { Text } = Typography;
const StyledEmptyText = styled(Text)`
  color: ${BLACK};
  opacity: 0.5;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium_large};
`;

const StyledTable = styled(Table)`
  ${(props) => commonBulkActionsTableStyle(props.isFilterListView, props.$hasSelection)}
  .ant-empty-image {
    height: 47px;
    margin-bottom: 0px;
    svg {
      height: 47px;
      width: 73px;
    }
  }
`;

const patientPath = ({ patientId, journeyId, papId, applicationId, claimId }) =>
  `/patient/${patientId}/journey/${journeyId}/applications/${papId}/${applicationId}/${Routes.CLAIMS}/${claimId}/${Routes.EDIT_CLAIM}`;

const SUBMITTED_DATE_LOCKED_STATUSES = [PENDING, OOP];
const POSTED_DATE_ALLOWED_STATUSES = [PAID];
const APPROVED_DATE_ALLOWED_STATUSES = [NEW_PENDING_PAYMENT, PAID];
const CHECK_NUMBER_ALLOWED_STATUSES = [PAID, NEW_PENDING_PAYMENT];

const renderWithLink = (patientRec, renderFn) => <LinkableCell to={patientPath(patientRec)}>{renderFn()}</LinkableCell>;

const renderEditableClaimOrLink = ({
  record,
  columnKey,
  editor,
  valueForEdit,
  displayNode,
  emptyHoverNode,
  commitFn,
  onEditStart,
  disableHoverFrame,
  disabled,
  disabledTooltip
}) => {
  if (!isEditableClaimColumn(columnKey)) {
    return renderWithLink(record, () => displayNode);
  }
  // Wrap in `renderWithLink` (LinkableCell = `<Link>` → real `<a href>`) so right-click,
  // cmd-click, middle-click all work natively. EditableCell's inner `enterEdit` calls
  // stopPropagation so editable-affordance clicks don't trigger Link's navigation.
  return renderWithLink(record, () => (
    <EditableCell
      value={valueForEdit}
      displayNode={displayNode}
      emptyHoverNode={emptyHoverNode}
      editor={editor}
      onCommit={(newValue) => commitFn(record, columnKey, newValue)}
      onEditStart={() => onEditStart && onEditStart(columnKey)}
      disableHoverFrame={disableHoverFrame}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      testId={`inline-edit-claim-${columnKey}-${record.claimId || record.id}`}
    />
  ));
};

const ClaimsTable = ({ updateSavedFilterViewParameters, isFilterListView, columnLayoutKey, rowSelection }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const tableColumns = useSelector(selectTableColumns);
  const isFetching = useSelector(selectIsFetching([ApiNames.AllClaimsPhiSearch, ApiNames.AllClaimsSearch]));
  const tableData = useSelector(selectAllClaimsData);
  const filtersModified = useSelector(selectFiltersModified);
  const setTableDataAct = (page, sorter) => dispatch(ACTION.paginateSortChange(page, selectedViews.allClaims, sorter));
  const totalRecordsCount = tableData?.totalPatients;
  const sortingEnabled = tableData?.sortingEnabled;
  const formatAmount = (amount) => (!isNull(amount) ? `$${formatToDecimal(amount) || 0}` : "");
  useTrackSavedListLayout(isFilterListView, columnLayoutKey);
  const { processColumns, components, containerRef, reorderGuideLeft, tableProps } = useColumnResizeAndReorder(
    columnLayoutKey ?? ColumnsKeys.tables["all-claims"],
    ["star"]
  );

  const { renderPatientCell, handleEditStart, lookups, assigneeOptions, facilityOptions, facilitiesList } =
    useTableInlineEdit({
      t,
      view: selectedViews.allClaims,
      patientActionCreator: ACTION.updateClaimsForPatientInPlace,
      renderWithLink
    });
  const { commit: commitClaim } = useInlineEditClaim(lookups);
  const getUserOrGroupById = useSelector(selectUserOrGroupById);

  const dateEmptyPlus = (key, record, ariaLabel) => (
    <InlineEmptyPlusButton
      testId={`inline-edit-empty-claim-${key}-${record.claimId || record.id}`}
      ariaLabel={ariaLabel}
    />
  );

  // Without a line item amount, only PENDING and OOP are selectable — `claimTotalAmount > 0` proxies
  // for "has a line item" since the table records don't carry the line items themselves.
  const buildClaimStatusOptions = (record) => {
    const hasAmount = Number(record?.claimTotalAmount) > 0;
    const allowedWhenNoLineItem = new Set([PENDING, OOP]);
    return claimStatusOptions.map((opt) => {
      const isDisabled = !hasAmount && !allowedWhenNoLineItem.has(opt.value);
      return {
        value: opt.value,
        label: opt.label,
        disabled: isDisabled,
        disabledTooltip: isDisabled ? t("inline_edit.claim_status_no_line_item") : undefined
      };
    });
  };

  const columns = [
    {
      ...tableColumns[selectedViews.allClaims].star,
      width: 35,
      className: "star-column",
      render: (text, record) => <StarColumn record={record} />
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].name_id, {
      sorterTooltipText: t("columns.sortById"),
      render: (text, record) => renderWithLink(record, () => <PatientNameColumn record={record} />)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allPatients]?.isDeceased, {
      render: (_, record) => renderPatientCell("isDeceased", record)
    }),
    {
      ...tableColumns[selectedViews.allClaims]?.phone_number,
      render: (text, record) => renderPatientCell("phone_number", record)
    },
    {
      ...tableColumns[selectedViews.allClaims]?.dob,
      render: (text, record) => renderPatientCell("dob", record)
    },

    {
      ...tableColumns[selectedViews.allClaims]?.ssn,
      render: (text, record) => renderPatientCell("ssn", record)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimDate, {
      render: (text, record) =>
        renderEditableClaimOrLink({
          record,
          columnKey: "claimDate",
          editor: <DateEditor />,
          valueForEdit: record.claimDate ? moment(record.claimDate) : null,
          displayNode: record.claimDate ? <div>{dateFormatter(record.claimDate)}</div> : null,
          emptyHoverNode: dateEmptyPlus("claimDate", record, t("inline_edit.claim_date")),
          commitFn: commitClaim,
          onEditStart: handleEditStart
        })
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimStatus, {
      render: (text, record) =>
        renderEditableClaimOrLink({
          record,
          columnKey: "claimStatus",
          editor: (
            <DropdownEditor
              options={buildClaimStatusOptions(record)}
              listHeight={180}
              placeholder={t("inline_edit.status_placeholder")}
              showArrow={false}
              suffixIcon={null}
              optionRender={(opt) => (
                <span
                  className="tm-status-pill-option"
                  style={{
                    ...getStatusTagStyle(opt.label, CLAIM_STATUS_COLORS),
                    "--tm-status-hover-bg": getStatusTagHoverBackground(opt.label, CLAIM_STATUS_COLORS),
                    "--tm-status-hover-border": getStatusTagHoverBorder(opt.label, CLAIM_STATUS_COLORS)
                  }}
                >
                  {opt.label}
                </span>
              )}
            />
          ),
          valueForEdit: record.claimStatus,
          displayNode: record.claimStatus ? <ClaimStatusColumn status={record.claimStatus} /> : null,
          commitFn: commitClaim,
          onEditStart: handleEditStart
        })
    }),

    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimFollowupDate, {
      render: (text, record) =>
        renderEditableClaimOrLink({
          record,
          columnKey: "claimFollowupDate",
          editor: <DateEditor />,
          valueForEdit: record.claimFollowupDate ? moment(record.claimFollowupDate) : null,
          displayNode: record.claimFollowupDate ? dateFormatter(record.claimFollowupDate) : null,
          emptyHoverNode: dateEmptyPlus("claimFollowupDate", record, t("inline_edit.claim_followup_date")),
          commitFn: commitClaim,
          onEditStart: handleEditStart
        })
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimSubmittedDate, {
      render: (text, record) => {
        const isLocked = SUBMITTED_DATE_LOCKED_STATUSES.includes(record.claimStatus);
        return renderEditableClaimOrLink({
          record,
          columnKey: "claimSubmittedDate",
          editor: <DateEditor />,
          valueForEdit: record.claimSubmittedDate ? moment(record.claimSubmittedDate) : null,
          displayNode: record.claimSubmittedDate ? dateFormatter(record.claimSubmittedDate) : null,
          emptyHoverNode: dateEmptyPlus("claimSubmittedDate", record, t("inline_edit.claim_submitted_date")),
          commitFn: commitClaim,
          onEditStart: handleEditStart,
          disabled: isLocked,
          disabledTooltip: isLocked ? t("inline_edit.claim_submitted_date_locked_by_status") : null
        });
      }
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].serviceDate, {
      render: (text, record) => renderWithLink(record, () => <DateOfServiceColumn record={record} />)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimFacility, {
      render: (text, record) =>
        renderEditableClaimOrLink({
          record,
          columnKey: "claimFacility",
          editor: (
            <SearchableDropdownEditor
              options={facilityOptions}
              listHeight={180}
              placeholder={t("inline_edit.facility_placeholder")}
            />
          ),
          // Record holds the facility name (`claimProvider`); resolve to id for the editor.
          valueForEdit: (() => {
            const match = facilitiesList.find((f) => f.name === record.claimProvider);
            return match ? String(match.id) : null;
          })(),
          displayNode: record.claimProvider || null,
          emptyHoverNode: dateEmptyPlus("claimFacility", record, t("inline_edit.facility_placeholder")),
          commitFn: commitClaim,
          onEditStart: handleEditStart
        })
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].providedBy, {
      render: (_, record) => renderWithLink(record, () => record.providedBy)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].assistanceProgramName, {
      render: (_, record) => renderWithLink(record, () => record.assistanceProgramName)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].apType, {
      render: (_, record) => renderWithLink(record, () => record.apType)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimTotalAmount, {
      render: (text, record) => renderWithLink(record, () => formatAmount(record.claimTotalAmount))
    }),

    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimPaidAmount, {
      render: (text, record) =>
        renderWithLink(record, () => (
          <StyledGreenValue disableStyle={!record.claimPaidAmount}>
            {formatAmount(record.claimPaidAmount)}
          </StyledGreenValue>
        ))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].dateReceived, {
      render: (text, record) => {
        const isLocked = !POSTED_DATE_ALLOWED_STATUSES.includes(record.claimStatus);
        return renderEditableClaimOrLink({
          record,
          columnKey: "dateReceived",
          editor: <DateEditor />,
          valueForEdit: record.claimReceivedDate ? moment(record.claimReceivedDate) : null,
          displayNode: record.claimReceivedDate ? dateFormatter(record.claimReceivedDate) : null,
          emptyHoverNode: dateEmptyPlus("dateReceived", record, t("inline_edit.posted_date")),
          commitFn: commitClaim,
          onEditStart: handleEditStart,
          disabled: isLocked,
          disabledTooltip: isLocked ? t("inline_edit.claim_posted_date_locked_by_status") : null
        });
      }
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].approvedDate, {
      render: (text, record) => {
        const isLocked = !APPROVED_DATE_ALLOWED_STATUSES.includes(record.claimStatus);
        return renderEditableClaimOrLink({
          record,
          columnKey: "approvedDate",
          editor: <DateEditor />,
          valueForEdit: record.approvedDate ? moment(record.approvedDate) : null,
          displayNode: record.approvedDate ? dateFormatter(record.approvedDate) : null,
          emptyHoverNode: dateEmptyPlus("approvedDate", record, t("inline_edit.claim_approved_date")),
          commitFn: commitClaim,
          onEditStart: handleEditStart,
          disabled: isLocked,
          disabledTooltip: isLocked ? t("inline_edit.claim_approved_date_locked_by_status") : null
        });
      }
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimDeniedAmount, {
      render: (text, record) =>
        renderWithLink(record, () => (
          <StyledRedValue disableStyle={!record.claimDeniedAmount}>
            {formatAmount(record.claimDeniedAmount)}
          </StyledRedValue>
        ))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].ClaimPatientResponsibilityAmount, {
      render: (text, record) =>
        renderWithLink(record, () => <div>{formatAmount(record.ClaimPatientResponsibilityAmount)}</div>)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims]?.balance, {
      render: (test, record) =>
        renderWithLink(record, () => <div>{!isNull(record.claimTotalAmount) && formatAmount(record.balance)}</div>)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].fundBalance, {
      render: (text, record) =>
        renderWithLink(record, () => <div>{`$${formatToDecimal(record.fundBalance) || 0}`}</div>)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].checkNumber_ACH, {
      render: (test, record) => {
        const isLocked = !CHECK_NUMBER_ALLOWED_STATUSES.includes(record.claimStatus);
        return renderEditableClaimOrLink({
          record,
          columnKey: "checkNumber_ACH",
          editor: <TextEditor placeholder={t("inline_edit.check_number_placeholder")} maxLength={50} />,
          valueForEdit: record.checkNumber || "",
          displayNode: record.checkNumber || null,
          emptyHoverNode: dateEmptyPlus("checkNumber_ACH", record, t("inline_edit.check_number_placeholder")),
          commitFn: commitClaim,
          onEditStart: handleEditStart,
          disabled: isLocked,
          disabledTooltip: isLocked ? t("inline_edit.claim_check_number_locked_by_status") : null
        });
      }
    }),

    {
      ...tableColumns[selectedViews.allClaims].medications,
      render: (text, record) =>
        renderWithLink(record, () => <MedicationsColumnComponent drugs={record.claimMedications} />)
    },
    {
      ...tableColumns[selectedViews.allClaims].facility,
      sorter: false,
      render: (_, record) => renderPatientCell("facility", record)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].patientStatus, {
      render: (text, record) => renderPatientCell("patientStatus", record)
    }),
    {
      ...tableColumns[selectedViews.allClaims]?.claimAssignee,
      render: (text, record) => {
        const { valueForEdit, options, resolved } = buildAssigneeEditProps({
          id: record.claimAssignee,
          assigneeOptions,
          getUserOrGroupById
        });
        return renderEditableClaimOrLink({
          record,
          columnKey: "claimAssignee",
          editor: (
            <SearchableDropdownEditor
              options={options}
              suffixIcon={<SearchOutlined />}
              placeholder={t("inline_edit.assignee_placeholder")}
            />
          ),
          valueForEdit,
          displayNode: resolved ? <ClaimAssigneeColumn record={record} /> : null,
          emptyHoverNode: dateEmptyPlus("claimAssignee", record, t("inline_edit.assignee_placeholder")),
          commitFn: commitClaim,
          onEditStart: handleEditStart
        });
      }
    },
    {
      ...tableColumns[selectedViews.allApplications]?.enrollmentAssignee,
      render: (text, record) => {
        const { valueForEdit, options, resolved } = buildAssigneeEditProps({
          id: record.enrollmentAssignee,
          assigneeOptions,
          getUserOrGroupById
        });
        return renderEditableClaimOrLink({
          record,
          columnKey: "enrollmentAssignee",
          editor: (
            <SearchableDropdownEditor
              options={options}
              suffixIcon={<SearchOutlined />}
              placeholder={t("inline_edit.assignee_placeholder")}
            />
          ),
          valueForEdit,
          displayNode: resolved ? <EnrollmentAssigneeColumn record={record} /> : null,
          emptyHoverNode: dateEmptyPlus("enrollmentAssignee", record, t("inline_edit.assignee_placeholder")),
          commitFn: commitClaim,
          onEditStart: handleEditStart
        });
      }
    },
    {
      ...tableColumns[selectedViews.allClaims].assignee,
      render: (text, record) => renderPatientCell("assignee", record)
    },
    { ...tableColumns[selectedViews.allClaims].drug },
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].physician, {
      render: (text, record) => renderPatientCell("physician", record)
    }),

    {
      ...tableColumns[selectedViews.allClaims].agreementSignatureDate,
      render: (text, record) =>
        renderWithLink(
          record,
          () => moment(record.activeConsentDate).isValid() && dateFormatter(record.activeConsentDate)
        )
    },
    {
      ...tableColumns[selectedViews.allClaims].consentStatus,
      render: (text, record) => renderWithLink(record, () => <ConsentStatusColumn record={record} />)
    },
    withSorting(sortingEnabled, tableColumns[selectedViews.allPatients]?.followupDate, {
      render: (text, record) => renderPatientCell("followupDate", record)
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].primaryInsurancePayer, {
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.primary)(text, record))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].secondaryInsurancePayer, {
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.secondary)(text, record))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].tertiaryInsurancePayer, {
      render: (text, record) =>
        renderWithLink(record, () => patientInsuranceCell(SUPPORTED_COVERAGES.tertiary)(text, record))
    }),
    withSorting(sortingEnabled, tableColumns[selectedViews.allClaims].claimTimeSinceLastActivity, {
      render: (text, record) =>
        renderWithLink(
          record,
          () => record.claimTimeSinceLastActivity && dateFormatter(record.claimTimeSinceLastActivity)
        )
    }),
    {
      ...tableColumns[selectedViews.allClaims]?.claimLastSavedBy,
      render: (text, record) => renderWithLink(record, () => <ClaimLastSavedByColumn record={record} />)
    }
  ];

  return (
    <>
      {!!tableData?.patients?.length && (
        <ColumnSelectorContainer>
          <ColumnSelector />
        </ColumnSelectorContainer>
      )}
      <TableColumnWrapper containerRef={containerRef} reorderGuideLeft={reorderGuideLeft}>
        <StyledTable
          {...tableProps}
          size="small"
          rowClassName="cursor-pointer"
          isFilterListView={isFilterListView}
          $hasSelection={rowSelection?.selectedRowKeys?.length > 0}
          style={{ width: "100%" }}
          loading={{ indicator: <TailormedSpinner />, spinning: isFetching }}
          components={components}
          columns={processColumns(columns.filter((col) => tableColumns[selectedViews.allClaims]?.[col.key]?.display))}
          rowKey="claimId"
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: (event) => handleRowNavigation(event, patientPath(record), history)
          })}
          locale={{
            emptyText: (
              <Empty
                image={<ClaimsIcon />}
                description={
                  <Space direction="vertical" size={0}>
                    <StyledEmptyText>
                      {!isFetching
                        ? filtersModified
                          ? t("patientApplications.claims_tab.no_claims_found")
                          : t("patientApplications.claims_tab.no_claims_yet")
                        : " "}
                    </StyledEmptyText>
                  </Space>
                }
              ></Empty>
            )
          }}
          onChange={(pagination, filters, sorter) =>
            fetchTableData(pagination, sorter, tableData, setTableDataAct, updateSavedFilterViewParameters)
          }
          pagination={{
            size: "small",
            showSizeChanger: false,
            current: tableData && tableData.selectedPage,
            defaultPageSize: PAGE_SIZE,
            pageSize: PAGE_SIZE,
            position: ["bottom"],
            total: totalRecordsCount,
            showTotal: (total, range) => (
              <div style={{ display: "flex", flexDirection: "row" }}>
                <AllClaimsPageCount total={total} range={range} />
              </div>
            )
          }}
          dataSource={tableData?.patients || null}
        />
      </TableColumnWrapper>
    </>
  );
};

export const AllClaimsTable = ClaimsTable;
